/**
 * useClientWaivers Hook
 *
 * Manages signed waivers for a specific client.
 * Handles waiver signing, validation, and status checks.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SignatureType = "drawn" | "typed";

export interface SignedWaiver {
  id: string;
  template_id: string;
  client_id: string;
  organization_id: string;
  signed_at: string;
  expires_at: string | null;
  signature_data: string;
  signature_type: SignatureType;
  initials_data: string | null;
  ip_address: string | null;
  user_agent: string | null;
  pdf_storage_path: string | null;
  is_valid: boolean;
  revoked_at: string | null;
  revoked_by: string | null;
  // Joined template data
  template?: {
    name: string;
    description: string | null;
    waiver_type: string;
    content: string;
  };
}

export interface SignWaiverInput {
  template_id: string;
  signature_data: string;
  signature_type: SignatureType;
  initials_data?: string;
}

interface ClientWaiverStatus {
  hasAllRequired: boolean;
  missingRequired: string[];
  expiringWaivers: SignedWaiver[];
  expiredWaivers: SignedWaiver[];
}

interface UseClientWaiversReturn {
  signedWaivers: SignedWaiver[];
  loading: boolean;
  error: string | null;
  signing: boolean;
  signWaiver: (input: SignWaiverInput) => Promise<SignedWaiver | null>;
  revokeWaiver: (waiverId: string, reason?: string) => Promise<boolean>;
  checkWaiverStatus: () => ClientWaiverStatus;
  hasValidWaiver: (templateId: string) => boolean;
  refetch: () => Promise<void>;
}

export function useClientWaivers(clientId: string | null): UseClientWaiversReturn {
  const [signedWaivers, setSignedWaivers] = useState<SignedWaiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiredTemplates, setRequiredTemplates] = useState<string[]>([]);

  const fetchWaivers = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      setSignedWaivers([]);
      return;
    }

    try {
      setError(null);

      // Fetch signed waivers with template info
      const { data: waivers, error: waiversError } = await supabase
        .from("signed_waivers")
        .select(`
          *,
          template:waiver_templates (
            name,
            description,
            waiver_type,
            content
          )
        `)
        .eq("client_id", clientId)
        .eq("is_valid", true)
        .order("signed_at", { ascending: false });

      if (waiversError) throw waiversError;

      setSignedWaivers((waivers as SignedWaiver[]) || []);

      // Fetch required templates for onboarding
      const { data: client } = await supabase
        .from("clients")
        .select("organization_id")
        .eq("id", clientId)
        .single();

      if (client?.organization_id) {
        const { data: templates } = await supabase
          .from("waiver_templates")
          .select("id")
          .eq("organization_id", client.organization_id)
          .eq("is_required_for_onboarding", true)
          .eq("is_active", true);

        setRequiredTemplates((templates || []).map((t) => t.id));
      }
    } catch (err) {
      console.error("Error fetching waivers:", err);
      setError("Failed to load waivers");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchWaivers();
  }, [fetchWaivers]);

  const signWaiver = async (input: SignWaiverInput): Promise<SignedWaiver | null> => {
    if (!clientId) return null;

    setSigning(true);
    try {
      // Get template to determine expiry
      const { data: template } = await supabase
        .from("waiver_templates")
        .select("expiry_days, organization_id")
        .eq("id", input.template_id)
        .single();

      if (!template) throw new Error("Template not found");

      // Calculate expiry date if applicable
      let expiresAt: string | null = null;
      if (template.expiry_days) {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + template.expiry_days);
        expiresAt = expiry.toISOString();
      }

      const { data, error } = await supabase
        .from("signed_waivers")
        .insert({
          template_id: input.template_id,
          client_id: clientId,
          organization_id: template.organization_id,
          signature_data: input.signature_data,
          signature_type: input.signature_type,
          initials_data: input.initials_data || null,
          expires_at: expiresAt,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          is_valid: true,
        })
        .select(`
          *,
          template:waiver_templates (
            name,
            description,
            waiver_type,
            content
          )
        `)
        .single();

      if (error) throw error;

      await fetchWaivers();
      return data as SignedWaiver;
    } catch (err) {
      console.error("Error signing waiver:", err);
      return null;
    } finally {
      setSigning(false);
    }
  };

  const revokeWaiver = async (waiverId: string, reason?: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from("signed_waivers")
        .update({
          is_valid: false,
          revoked_at: new Date().toISOString(),
          revoked_by: user?.id || null,
        })
        .eq("id", waiverId);

      if (error) throw error;

      await fetchWaivers();
      return true;
    } catch (err) {
      console.error("Error revoking waiver:", err);
      return false;
    }
  };

  const hasValidWaiver = (templateId: string): boolean => {
    const now = new Date();
    return signedWaivers.some(
      (w) =>
        w.template_id === templateId &&
        w.is_valid &&
        (!w.expires_at || new Date(w.expires_at) > now)
    );
  };

  const checkWaiverStatus = (): ClientWaiverStatus => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Find missing required waivers
    const missingRequired = requiredTemplates.filter(
      (templateId) => !hasValidWaiver(templateId)
    );

    // Find waivers expiring in the next 30 days
    const expiringWaivers = signedWaivers.filter(
      (w) =>
        w.is_valid &&
        w.expires_at &&
        new Date(w.expires_at) > now &&
        new Date(w.expires_at) <= thirtyDaysFromNow
    );

    // Find expired waivers
    const expiredWaivers = signedWaivers.filter(
      (w) => w.expires_at && new Date(w.expires_at) <= now
    );

    return {
      hasAllRequired: missingRequired.length === 0,
      missingRequired,
      expiringWaivers,
      expiredWaivers,
    };
  };

  return {
    signedWaivers,
    loading,
    error,
    signing,
    signWaiver,
    revokeWaiver,
    checkWaiverStatus,
    hasValidWaiver,
    refetch: fetchWaivers,
  };
}
